import {CommLibInstance, Deferred, EventBusInstance, LegState} from "@sap/cpaas-commlib-lib";
import LZString from "lz-string";
import {AppState, Configuration, Parameter} from "./configuration";
import {Logger} from "./logger";
import {App} from "../App";

export abstract class MessageProvider {

	private static messages: any = {};
	private static messageCount: number = 0;
	private static parentUri: string;

	static setup() {
		window.addEventListener("message", MessageProvider.onMessage, true);
		MessageProvider.readSearch();
	}

	static destroy() {
		window.removeEventListener("message", MessageProvider.onMessage, true);
	}

	static sendMessage(type: MessageType, content?: any, id?: number): IMessage {
		if (!MessageProvider.parentUri) {
			return;
		}
		const messageObject: IMessage = { id: id || -1, type, content };
		let message;
		try {
			message = JSON.stringify(messageObject);
		} catch (e) {
			Logger.error("");
		}
		window.parent.postMessage(message, MessageProvider.parentUri);
		return messageObject;
	}

	static async sendMessageWaitResponse(type: MessageType, content?: any): Promise<IMessage> {
		if (!MessageProvider.parentUri) {
			return;
		}
		this.messageCount++;
		const message: IMessage = MessageProvider.sendMessage(type, content, this.messageCount);
		message.deferred = new Deferred();
		MessageProvider.messages[MessageProvider.messageCount] = message;
		return message.deferred.promise;
	}

	private static readSearch() {
		const search = location.search.split("?").splice(1).join("?");
		const parts = search.split("&");
		const itemMap: any = {};
		for (const part of parts) {
			const item = part.split("=");
			if (item.length !== 2) {
				continue;
			}
			const component = decodeURIComponent(item[1]);
			if (item[0] === Parameter.parentUri) {
				MessageProvider.parentUri = component;
			} else if (item[0] === Parameter.chatQueues || item[0] === Parameter.queues) {
				itemMap[item[0]] = JSON.parse(component);
			} else if (isNaN((component as any)) === false) {
				itemMap[item[0]] = parseFloat(component);
			} else if (component === "false" || component === "true") {
				itemMap[item[0]] = item[1] === "true";
			} else {
				itemMap[item[0]] = component;
			}
		}
		Configuration.setProperties(itemMap);
	}

	private static onMessage(event: MessageEvent) {
		if (event.origin !== MessageProvider.parentUri) {
			return;
		}
		let message: IMessage = event.data;

		// tslint:disable-next-line:prefer-const
		let reply = null;
		switch (message.type) {
			case MessageType.reply:
				// Callback from the parent
				const originalMessage: IMessage = MessageProvider.messages[message.id];
				originalMessage.deferred.resolve(message);
				delete MessageProvider.messages[message.id];
				return;
			case MessageType.configuration:
				if (message.content) {
					for (const key in message.content) {
						if (message.content.hasOwnProperty(key)) {
							Configuration.setProperty(key, message.content[key]);
						}
					}
				}
				break;
			case MessageType.info:
				const leg = AppState.activeLeg;
				if (!leg) {
					reply = {error: "No interaction started from client"};
					break;
				}
				const participants = leg.getContact()?.getLegs().map(l => l.getAddress());
				participants.push(Configuration.userName || "visitor")
				const sendInfoReply = (max:number, count = 0) => () => {
					const legConnector = leg.getConnector();
					if (legConnector.length === 32) {
						clearInterval(waitInteractionId);
						reply = {
							id: legConnector,
							transcript: AppState.messages.map((m) => ({
								alias: m.alias,
								content: m.content,
								time: m.time
							})),
							state: LegState[leg.getState()],
							cad: leg.getContact()?.getCad(),
							participants,
						};
						MessageProvider.sendMessage(MessageType.reply, reply, message.id);
					}
					else if (count === max) {
						const errMsg = "Interaction id not available";
						Logger.error("MessageProvider::" + errMsg);
						MessageProvider.sendMessage(MessageType.reply, {error: errMsg}, message.id);
						clearInterval(waitInteractionId);
						return;
					}
					else {
						count++;
						Logger.trace("MessageProvider::Waiting for interaction id: "+count+"/"+max);
					}
				};
				// Wait needed to ensure leg connector value is real interaction id (CC365V1-4928)
				const waitInteractionId = setInterval(sendInfoReply(20),50); // Allow 20 * 50ms = 1sec for EcfAdapter to provide real id
				return;
			case MessageType.logs:
				CommLibInstance.downloadLogs();
				break;
			case MessageType.state:
				break;
			case MessageType.startWebChat:
				EventBusInstance.publish(Configuration.startWebChatEvent, undefined, Configuration.component);
				break;
			case MessageType.startLaunchpad:
				EventBusInstance.publish(Configuration.startLaunchpadEvent, undefined, Configuration.component);
				break;				
			case MessageType.resize:
				EventBusInstance.publish(Configuration.sizeEvent, message.content, Configuration.component);
				break;
			case MessageType.resizeEventFrameNotResized:
				EventBusInstance.publish(Configuration.sizeEventFrameNotResized, message.content, Configuration.component);
				break;				
			case MessageType.load:
				let settings;
				try {
					settings = JSON.parse(LZString.decompressFromEncodedURIComponent(message.content.encoded));
				} catch (e) {
					return;
				}
				// Configure app with initial load-time settings
				Configuration.setProperties({ ...settings, ...message.content.config });
				// Loading complete => app ready
				MessageProvider.sendMessage(MessageType.ready, settings);
				break;
			case MessageType.upgradeToWebChat:
				EventBusInstance.publish(Configuration.upgradeToWebChat, undefined, Configuration.component);
				break;
			case MessageType.minimized:
				EventBusInstance.publish(Configuration.minimizeEvent, undefined, Configuration.component);
				break;
			case MessageType.orientation:
				EventBusInstance.publish(Configuration.orientationChange, message.content, Configuration.component);
				break;
			default:
				return;
		}

		if (message.id !== -1) {
			// Parent expects callback
			MessageProvider.sendMessage(MessageType.reply, reply, message.id);
		}
	}
}

export interface IMessage {
	id: number;
	type: MessageType;
	content: any;
	deferred?: Deferred;
}

export enum MessageType {
	state = "state", configuration = "configuration", startWebChat = "startWebChat", startLaunchpad = "startLaunchpad", reply = "reply", load = "load", resize = "resize", resizeEventFrameNotResized = "resizeEventFrameNotResized", move = "move", ready = "ready", event = "event", visible = "visible",
	animate = "animate", logs = "logs", minimized = "minimized", active = "active", info = "info", dock = "dock", style = "style", launchpad = "launchpad", upgradeToWebChat = "upgradeToWebChat", openLink = "openLink", orientation = "orientation", 
}

export interface IStyleMessage {
	targetElementId: string;
	style: Record<string, string>
}